import React from 'react';
import { Modal, Spin, Switch } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    GET_CLIENT_CARTON_BATCH_INBOUND_CHANNEL_OPTIONS,
    GetClientCartonBatchInboundChannelOptionsResponse,
} from 'graphql/queries/getClientCartonBatchInboundChannelOptions';

/** 解析 warehouse.clientCartonBatchInboundChannelsJson；null/空/解析失败按空集处理（与后端判定一致）；元素兼容数字与数字字符串 */
export const parseBatchInboundChannelIds = (json?: string | null): number[] => {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
    } catch {
        return [];
    }
};

interface Props {
    channelsJson?: string | null;
    disabled: boolean;
    saving: boolean;
    /** 用户确认后回调，参数为新的渠道 JSON（如 "[109,224]"） */
    onSave: (channelsJson: string) => void;
}

/**
 * Hub 配置「按大包批量入库」渠道开关列表（替代原单一 boolean 开关）。
 * 候选渠道来自 common_config client_carton.batch_inbound.channel_options。
 */
const ClientCartonBatchInboundChannelsConfig: React.FC<Props> = ({ channelsJson, disabled, saving, onSave }) => {
    const { t } = useTranslation();
    const { data, loading, error } = useQuery<GetClientCartonBatchInboundChannelOptionsResponse>(
        GET_CLIENT_CARTON_BATCH_INBOUND_CHANNEL_OPTIONS
    );
    const options = data?.getClientCartonBatchInboundChannelOptions ?? [];
    const enabledIds = new Set(parseBatchInboundChannelIds(channelsJson));

    const handleChange = (channelId: number, channelName: string, checked: boolean) => {
        Modal.confirm({
            title: t('central.sites.details.hubConfigConfirmTitle'),
            content: checked
                ? t('central.sites.details.hubConfigConfirmEnableChannelContent', { channelName })
                : t('central.sites.details.hubConfigConfirmDisableChannelContent', { channelName }),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            onOk: () => {
                const next = new Set(enabledIds);
                if (checked) {
                    next.add(channelId);
                } else {
                    next.delete(channelId);
                }
                // 只保留仍在候选集内的渠道，顺序按候选集排列，保证 JSON 稳定；
                // channelId 必须转 number（GraphQL Long 响应为字符串），否则写出 ["65"] 且回读匹配失败
                const nextJson = JSON.stringify(
                    options.map((o) => Number(o.channelId)).filter((id) => next.has(id))
                );
                onSave(nextJson);
            },
        });
    };

    // 接口故障与"候选集真未配置"分开提示，避免误导运维去改配置
    if (error) {
        return <div className="text-red-500">{t('central.sites.details.clientCartonBatchInboundLoadFailed')}</div>;
    }
    if (!loading && options.length === 0) {
        return <div className="text-gray-500">{t('central.sites.details.clientCartonBatchInboundNoChannelOptions')}</div>;
    }

    return (
        // 候选集加载中给遮罩：网慢时该区域只剩一行标题、零开关行，会被误读成"没配置/没打开"
        <Spin spinning={loading}>
        <div className={`flex flex-col gap-2 ${loading ? 'min-h-[120px]' : ''}`}>
            <span>{t('central.sites.details.clientCartonBatchInbound')}:</span>
            {options.map((option) => {
                const channelId = Number(option.channelId);
                const checked = enabledIds.has(channelId);
                return (
                    <div key={option.channelId} className="flex items-center gap-3 pl-4">
                        <span className="w-48">{option.channelName}</span>
                        <Switch
                            checked={checked}
                            onChange={(nextChecked) => handleChange(channelId, option.channelName, nextChecked)}
                            disabled={disabled || saving}
                            loading={saving}
                        />
                        <span className="text-gray-500">
                            {checked ? t('central.sites.details.enabled') : t('central.sites.details.disabled')}
                        </span>
                    </div>
                );
            })}
        </div>
        </Spin>
    );
};

export default ClientCartonBatchInboundChannelsConfig;
